/* eslint-disable no-restricted-syntax */
import i18n from 'i18next';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { PageType } from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import { BaseAnalyzer } from '../base/base-analyzer';
import { IReview, IParagraphReview, MachineReviewAnalyzer } from './machine-review-analyzer';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { SlideInterface } from '@tencent/docs-api';

type IResult = SlideInterface.CheckResultItem;

interface IPageInfo {
    type: PageType;
    useLayout?: boolean;
}

interface ISameElements {
    elements: { element: IElementBaseModel; pageIndex: number }[];
}

const pageNames = {
    [PageType.CoverPage]: i18n.t('封面'),
    [PageType.TableOfContents]: i18n.t('目录'),
    [PageType.SectionDivider]: i18n.t('章节'),
    [PageType.Acknowledgments]: i18n.t('感谢'),
};

/**
 * 母版版式检查规则：
 * 1. 母版名称是否合规：风格+颜色
 * 2. 母版是否包含封面页、目录页、章节页、结束页
 */
export class MachineRepeatAnalyzer extends BaseAnalyzer<IResult> {
    private title = i18n.t('重复元素检测');
    private description = i18n.t('检查重复元素，重复元素建议放在版式里');
    private level = 'warning' as const;
    private anchorType = 'element' as const;

    public constructor(private reviewAnalyzer: MachineReviewAnalyzer) {
        super();
    }

    public async calculateResult(presentation: IPresentationModel): Promise<IResult> {
        const review = await this.reviewAnalyzer.result;
        const pageInfos = this.getPageInfos(review as IReview);
        const slideList = presentation.children;
        // 检查是否使用了对应版式
        slideList.forEach((slide, index) => {
            const pageType = pageInfos[index].type;
            if (pageType === PageType.Content) {
                return;
            }
            const layout = slide.getRelationLayoutModel();
            const layoutName = layout?.name;
            if (layoutName?.includes(pageNames[pageType])) {
                pageInfos[index].useLayout = true;
            } else {
                pageInfos[index].useLayout = false;
            }
        });
        // 检查 section 的重复元素
        const sectionPages = slideList.filter((_, index) => pageInfos[index].type === PageType.SectionDivider);
        const sectionSameElements = this.sameWithShape(sectionPages);
        // 检查 content 的重复元素
        const contentPages = slideList.filter((_, index) => pageInfos[index].type === PageType.Content);
        const contentSameElements = this.sameWithShape(
            contentPages,
            Math.floor(contentPages.length * 0.8),
            contentPages.length,
        );

        // 重新组织结果用于输出
        const passed = sectionSameElements.length <= 0 && contentSameElements.length <= 0;
        if (passed) {
            return { passed, title: this.title, description: this.description };
        }
        const errorMessages: SlideInterface.CheckErrorMessage[] = [];
        const { level, anchorType } = this;
        for (const { elements } of sectionSameElements) {
            errorMessages.push({
                level,
                anchorType,
                anchor: elements[0].element,
                description: i18n.t(`页[${elements.map(i => i.pageIndex + 1)}]相同的元素，建议在章节页版式里统一设置`),
            });
        }
        for (const { elements } of contentSameElements) {
            errorMessages.push({
                level,
                anchorType,
                anchor: elements[0].element,
                description: i18n.t(`页[${elements.map(i => i.pageIndex + 1)}]相同的元素，建议在版式里统一设置`),
            });
        }
        return { passed, title: this.title, description: this.description, errorMessages };
    }

    private sameWithShape(pages: ISlideModel[], minElements = 2, startPage = 1): ISameElements[] {
        const sameElements: ISameElements[] = [];
        pages.slice(0, startPage).forEach((slide, index) => {
            slide.children.forEach((shape) => {
                // 文字过多的元素不比较
                if (shape.type === UIModelType.SHAPE) {
                    const length = (shape as IShapeModel).paragraphList?.reduce((res, cur) => {
                        const textLength = cur.textRuns?.reduce((res, cur) => res + (cur.text?.length ?? 0), 0);
                        return res + (textLength ?? 0);
                    }, 0);
                    if ((length ?? 0) > 12) {
                        return;
                    }
                }
                // 占位符不检测
                if (shape.isPlaceholder || shape.isCustomPlaceholder) {
                    return;
                }
                const curSameElements: { element: IElementBaseModel; pageIndex: number }[] = [
                    { element: shape, pageIndex: shape.slideRef?.listIndex ?? -1 },
                ];
                // 遍历所有shape
                pages
                    .filter((_, i) => i > index)
                    .forEach((otherSlide) => {
                        otherSlide.children.forEach((otherShape) => {
                            // 出现过的元素不再检测
                            if (
                                sameElements.flatMap(list => list.elements.map(i => i.element)).includes(otherShape)
                            ) {
                                return;
                            }
                            if (
                                this.isSameRect(shape.props.style, otherShape.props.style, {
                                    width: true,
                                    height: true,
                                })
                            ) {
                                curSameElements.push({
                                    element: otherShape,
                                    pageIndex: otherShape.slideRef?.listIndex ?? -1,
                                });
                            }
                        });
                    });
                if (curSameElements.length >= minElements) {
                    sameElements.push({ elements: curSameElements });
                }
            });
        });
        return sameElements;
    }

    private getPageInfos(review: IReview): IPageInfo[] {
        let contentsPageIndex;
        let sectionPageIndex;
        return review.pages.map((page, index) => {
            // 第一页认为是封面
            if (index === 0) {
                return { type: PageType.CoverPage };
            }
            // 最后两页有谢谢是感谢页
            const hasEaThank = str => str.text.replaceAll(/\s/g, '').includes('谢');
            const hasLatinThank = str => str.text.replaceAll(/\s/g, '').toLocaleLowerCase()
                .includes('thank');
            if (index >= review.pages.length - 1) {
                if (page.bodyTexts.some(i => hasEaThank(i) || hasLatinThank(i))) {
                    return { type: PageType.Acknowledgments };
                }
            }
            // 找目录页
            const hasEaContents = str => str.text.replaceAll(/\s/g, '').includes('目录');
            const hasLatinContents = str => str.text.replaceAll(/\s/g, '').toLocaleLowerCase()
                .includes('content');
            if (page.bodyTexts.some(i => hasEaContents(i) || hasLatinContents(i))) {
                contentsPageIndex = index;
                return { type: PageType.TableOfContents };
            }
            // 目录页后一个认为是章节页
            if (contentsPageIndex && contentsPageIndex + 1 === index) {
                sectionPageIndex = index;
                return { type: PageType.SectionDivider };
            }
            // 具有和第一个章节页相同结构的页也是章节页
            if (sectionPageIndex) {
                if (this.isSame(page.bodyTexts, review.pages[sectionPageIndex].bodyTexts)) {
                    return { type: PageType.SectionDivider };
                }
            }
            return { type: PageType.Content };
        });
    }

    // 对比 rect
    private isSame = (paragraphs1: IParagraphReview[], paragraphs2: IParagraphReview[]) => {
        if (paragraphs1.length !== paragraphs2.length) {
            return false;
        }
        for (let i = 0; i < paragraphs1.length; i += 1) {
            return this.isSameRect(paragraphs1[i].rect, paragraphs2[i].rect);
        }
    };

    // eslint-disable-next-line complexity
    private isSameRect(rect1?: IRect, rect2?: IRect, options: { width?: boolean; height?: boolean } = {}) {
        const { left: left1 = 0, width: width1 = 0 } = rect1 ?? {};
        const { top: top1 = 0, height: height1 = 0 } = rect1 ?? {};
        const { left: left2 = 0, width: width2 = 0 } = rect2 ?? {};
        const { top: top2 = 0, height: height2 = 0 } = rect2 ?? {};

        // 宽度相似
        if (options.width && Math.abs(width1 - width2) > 0.1 * width1) {
            return false;
        }
        // 高度相似
        if (options.height && Math.abs(height1 - height2) > 0.1 * height1) {
            return false;
        }
        // 上对齐
        if (Math.abs(top1 - top2) > 0.1 * height1) {
            return false;
        }
        // 左对齐
        if (Math.abs(left1 - left2) < 0.1 * width1) {
            return true;
        }
        // 右对齐
        if (Math.abs(left1 + width1 - (left2 + width2)) < 0.1 * width1) {
            return true;
        }
        // 居中对齐
        const widthHalf1 = width1 / 2;
        const widthHalf2 = width2 / 2;
        if (Math.abs(widthHalf1 + left1 - (widthHalf2 + left2)) < 0.1 * width1) {
            return true;
        }
        return false;
    }
}
